<template>
  <div class="i-stripe-embedded-form">
    <div id="stripe-checkout"></div>
  </div>
</template>

<script setup lang="ts">
import { loadStripe, type Stripe, type StripeEmbeddedCheckout } from "@stripe/stripe-js"
import { onMounted, onUnmounted } from "vue"

// Services
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

// Types and Enums
import type StripePrice from "@/shared/enums/StripePrice"

const emit = defineEmits(["error"])

const props = defineProps({
  stripePrice: {
    type: String as () => StripePrice,
    required: true
  },
  userFlow: {
    type: String as () => UserFlow,
    required: true
  }
})

let stripeCheckout: StripeEmbeddedCheckout | null = null
let stripe: Stripe | null = null

async function initializeStripe() {
  let clientSecret: string

  try {
    console.log("Creating checkout session")
    const [checkoutResponse, stripeInstance] = await Promise.all([
      api.stripe.postCheckoutSessionEmbedded({ stripePrices: [props.stripePrice] }),
      loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
        apiVersion: "2024-06-20"
      })
    ])

    clientSecret = checkoutResponse?.clientSecret
    if (!clientSecret) {
      throw new Error("Client secret not found in response")
    }

    if (!stripeInstance) {
      throw new Error("Stripe not loaded")
    }

    stripe = stripeInstance

    stripeCheckout = await stripe.initEmbeddedCheckout({
      clientSecret
    })

    if (!stripeCheckout) {
      throw new Error("Stripe checkout not loaded")
    }

    stripeCheckout.mount("#stripe-checkout")
    await GtmService.beginCheckoutEvent(props.userFlow, checkoutResponse.items)
  } catch (error) {
    console.error("Error loading Stripe checkout")
    console.error(error)
    emit("error")
  }
}

function cleanupStripe() {
  if (stripeCheckout) {
    console.log("Cleaning up Stripe checkout")
    stripeCheckout.destroy()
  }
}

onMounted(() => {
  initializeStripe()
})

onUnmounted(() => {
  cleanupStripe()
})
</script>

<style lang="scss" scoped>
.i-stripe-embedded-form {
  width: 100%;

  #stripe-checkout {
    width: 100%;
    height: 100%;
    overflow: auto;
  }
}
</style>
